import { _ } from "../polyglot/polyglot.js";
import { convertCompanyTypeIconNameToIcon } from "../helpers/icon-util.js";
import { iconAndText, text } from "../elements/index.js";
import { notBlank } from "./forms/validators.js";
import { warnDialog } from "./dialogs/dialogs.js";
import { Widget } from "@foretagsplatsen/widgetjs";
import FormBuilder from "./forms/FormBuilder.js";
import optionsBag from "../../models/options/optionsBag.js";

/**
 * `companyInfoFormWidget` is a widget to displaying information
 * and input fields for a given company.
 *
 * @param {companyInfo} [spec.company] - Company to edit
 * @param {companyTypes[]} [spec.companyTypes] - List of company
 * types to choose from for the company
 * @param {user} [spec.currentUser] - Currently logged in user
 * @param {boolean} spec.isArchiveCompanyEnabled - `true` if the archiving button
 * must be displayed
 */
export default class CompanyInfoFormWidget extends Widget {
	constructor({
		company,
		companyTypes,
		currentUser,
		isArchiveCompanyEnabled,
	}) {
		super(...arguments);

		this._currentUser = currentUser;
		this._companyTypes = companyTypes;
		this._isArchiveCompanyEnabled = isArchiveCompanyEnabled;

		this.onSave = this._events.createEvent();
		this.onDelete = this._events.createEvent();
		this.onDataExtractionRequested = this._events.createEvent();
		this.onArchive = this._events.createEvent();

		if (company) {
			this.setCompany(company);
		}

		this._setupForm();
	}

	getCompany() {
		return this._company;
	}

	setCompany(newCompany) {
		this._company = newCompany;
		this._companyContactInfo = this._company.getContactInfo();
		this._setupForm();
		this.update();
	}

	renderContentOn(html) {
		if (!this._company) {
			return;
		}

		html.render(this._form);
	}

	_extraActions() {
		const actions = [];

		if (this._canUpgradeToStandard()) {
			actions.push(this._renderUpgradeToStandardButtonOn.bind(this));
		}

		if (this._canDowngradeToLight()) {
			actions.push(this._renderDowngradeToLightButtonOn.bind(this));
		}

		if (this._currentUser.canExtractDataFromCompany(this._company)) {
			actions.push(this._renderExtractDataButtonOn.bind(this));
		}

		if (this._currentUser.canDeleteCompany(this._company)) {
			actions.push(this._renderDeleteButtonOn.bind(this));
		}

		if (
			this._currentUser.canArchiveCompany() &&
			this._isArchiveCompanyEnabled
		) {
			actions.push(this._renderArchiveCompanyButtonOn.bind(this));
		}

		return actions;
	}

	_renderDeleteButtonOn(html) {
		html.a(
			{
				klass: "btn btn-danger",
				click: this._deleteCompany.bind(this),
				"data-test": "delete-company",
			},
			_("Delete company"),
		);
	}

	_renderExtractDataButtonOn(html) {
		html.a(
			{
				klass: "btn btn-danger",
				click: this._requestCompanyDataExtraction.bind(this),
				"data-test": "extract-company-data",
			},
			_("Extract company data"),
		);
	}

	_renderArchiveCompanyButtonOn(html) {
		html.a(
			{
				klass: "btn btn-warning",
				click: this._requestArchiveCompany.bind(this),
				"data-test": "archive-company",
			},
			_("Archive"),
		);
	}

	_renderUpgradeToStandardButtonOn(html) {
		html.a(
			{
				klass: "btn btn-default",
				click: this._upgradeToStandard.bind(this),
				"data-test": "upgrade-company",
			},
			_("Upgrade"),
		);
	}

	_renderDowngradeToLightButtonOn(html) {
		html.a(
			{
				klass: "btn btn-default",
				click: this._downgradeToLight.bind(this),
				"data-test": "downgrade-company",
			},
			_("Downgrade"),
		);
	}

	_deleteCompany() {
		this._confirmDeletion({
			name: this._company.getName(),
			contentCssClass: "dark-dialog",
			callback: () => this.onDelete.trigger(this._company),
		});
	}

	_requestCompanyDataExtraction() {
		this._confirm({
			title: _("Request data extraction"),
			contentCssClass: "dark-dialog",
			body: _(
				"Upon confirmation, a request to extract all company data will be triggered. You will receive an email with a link to the data once the extraction is completed.",
			),
			okLabel: _("Request company data"),
			callback: () =>
				this.onDataExtractionRequested.trigger(this._company),
		});
	}

	_requestArchiveCompany() {
		warnDialog({
			name: this._company.getName(),
			title: _("Archiving {name}", {
				name: this._company.getName(),
			}),
			body: (html) =>
				html.div(
					text(_("Be aware that by archiving this company")),
					html.ul(
						html.li(
							_(
								"Client users will be detached (and deleted if not attached to other companies)",
							),
						),
						html.li(_("Consultants will be detached")),
						html.li(
							_(
								"You will have no access to data unless you unarchive the company",
							),
						),
						html.li(_("You will be charged an archiving fee")),
					),
				),
			okLabel: _("Archive company"),
			callback: () => this.onArchive.trigger(),
		}).show();
	}

	_canUpgradeToStandard() {
		return (
			this._company.isLightCompany() &&
			this._currentUser.canUpgradeCompany(this._company)
		);
	}

	_canDowngradeToLight() {
		return (
			!this._company.isLightCompany() &&
			this._currentUser.canDowngradeCompany(this._company)
		);
	}

	_upgradeToStandard() {
		this._confirm({
			title: _("Upgrade {name}", {
				name: this._company.getName(),
			}),
			contentCssClass: "dark-dialog",
			body: _(
				"By upgrading this company, the company users will be able to access all the menu options, not only the Period report and Document archive as they currently do. If you need to downgrade again, please contact the support.",
			),
			confirmLabel: _("Upgrade"),
			callback: () => {
				this._company.upgradeToStandard();
				this.onSave.trigger(this._company);
				this._setupForm();
				this.update();
			},
		});
	}

	_downgradeToLight() {
		this._confirm({
			title: _("Downgrade {name}", {
				name: this._company.getName(),
			}),
			contentCssClass: "dark-dialog",
			body: _(
				"By downgrading this company, the company users will only be able to access Period report and Document archive.",
			),
			confirmLabel: _("Downgrade"),
			callback: () => {
				this._company.downgradeToLightCompany();
				this.onSave.trigger(this._company);
				this._setupForm();
				this.update();
			},
		});
	}

	_setupForm() {
		if (!this._company) {
			return;
		}

		this._form = new FormBuilder({
			saveAction: this.onSave.trigger,
			saveLabel: _("Save"),
			showCancel: false,
			patch: true,
			model: this._company,
			labelClass: "col-sm-4",
			inputClass: "col-sm-8",
			extraActions: this._extraActions.bind(this),
			dataTestName: "save-company-info",
		});

		this._setupBasicFields();
		this._setupCompanyTypeField();
		this._setupContactInformationFields();
		this._setupAuditorDetails();
	}

	_setupBasicFields() {
		this._form.input({
			disabled: true,
			accessor: this._company.getBusinessIdentityCode.bind(this._company),
			label: _("Business identity code"),
		});

		this._form.input({
			accessor: this._company.getName.bind(this._company),
			mutator: this._company.setName.bind(this._company),
			name: "name",
			label: _("Name"),
			validator: notBlank(),
		});

		this._form.input({
			accessor: this._company.getClientNumber.bind(this._company),
			mutator: this._company.setClientNumber.bind(this._company),
			label: _("Client number"),
		});

		this._form.input({
			accessor: this._company.getOfficeAffiliation.bind(this._company),
			mutator: this._company.setOfficeAffiliation.bind(this._company),
			label: _("Office affiliation"),
		});
	}

	_setupCompanyTypeField() {
		if (!this._companyTypes || this._companyTypes.length === 0) {
			return;
		}

		const companyTypeItems = this._companyTypes.map((companyType) => {
			const icon = convertCompanyTypeIconNameToIcon(
				companyType.getIcon(),
			);

			return {
				value: { id: companyType.getId() },
				label: iconAndText({ icon }, companyType.getName()),
			};
		});

		const defaultItem = {
			value: { id: null },
			label: _("Default company type of agency"),
		};

		const items = [defaultItem, ...companyTypeItems];
		const companyTypeId = this._company.getCompanyTypeId();

		this._form.newDropDownField({
			label: _("Company type"),
			items,
			accessor: () =>
				items.find(({ value }) => value.id === companyTypeId)?.value,
			mutator: ({ id }) => this._company.setCompanyTypeId(id),
		});
	}

	_setupContactInformationFields() {
		this._form.legend(_("Contact information"));

		this._form.input({
			accessor: this._companyContactInfo.getCustomerManager.bind(
				this._companyContactInfo,
			),
			mutator: this._companyContactInfo.setCustomerManager.bind(
				this._companyContactInfo,
			),
			label: _("Customer manager"),
		});

		this._form.input({
			accessor: this._companyContactInfo.getName.bind(
				this._companyContactInfo,
			),
			mutator: this._companyContactInfo.setName.bind(
				this._companyContactInfo,
			),
			label: _("Contact name"),
		});

		this._form.input({
			accessor: this._companyContactInfo.getAddress.bind(
				this._companyContactInfo,
			),
			mutator: this._companyContactInfo.setAddress.bind(
				this._companyContactInfo,
			),
			label: _("Address"),
		});

		this._form.input({
			accessor: this._companyContactInfo.getPostalAddress.bind(
				this._companyContactInfo,
			),
			mutator: this._companyContactInfo.setPostalAddress.bind(
				this._companyContactInfo,
			),
			label: _("Zip code & City"),
		});

		this._form.input({
			accessor: this._companyContactInfo.getPhone.bind(
				this._companyContactInfo,
			),
			mutator: this._companyContactInfo.setPhone.bind(
				this._companyContactInfo,
			),
			label: _("Phone number"),
		});
	}

	_setupAuditorDetails() {
		if (!optionsBag.fromId("disclaimer").isActive()) {
			return;
		}

		const auditor = this._company.getAuditor();

		this._form.legend(_("Auditor details"));

		this._form.input({
			accessor: auditor.getName.bind(auditor),
			mutator: auditor.setName.bind(auditor),
			label: _("Name"),
		});

		this._form.input({
			accessor: auditor.getCity.bind(auditor),
			mutator: auditor.setCity.bind(auditor),
			label: _("City"),
		});

		this._form.input({
			accessor: auditor.getTitle.bind(auditor),
			mutator: auditor.setTitle.bind(auditor),
			label: _("Title"),
		});

		this._form.input({
			accessor: auditor.getRegistrationNumber.bind(auditor),
			mutator: auditor.setRegistrationNumber.bind(auditor),
			label: _("Registration number"),
		});

		this._form.input({
			accessor: auditor.getOrganizationNumber.bind(auditor),
			mutator: auditor.setOrganizationNumber.bind(auditor),
			label: _("Organization number"),
		});
	}
}
